import { useInvestmentAccountActivity, useWealthOverview, type InvestmentActivityDetailV2 } from '@egw/api'
import { Button, ButtonVariant, PageHeader } from '@egw/components'
import { WEALTH_ROUTES } from '@egw/constants'
import { useTranslation } from '@egw/i18n'
import { InfiniteScrollWrapper } from '@egw/layouts/src/components'
import { Currency, getCurrencySymbol, getLastRefreshedText } from '@egw/utils'
import { InformationCircleIcon } from '@heroicons/react/24/solid'
import { useCallback, useMemo, useState, type FC } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import { useNavigate, useParams } from 'react-router'
import { TransactionInvestmentItem } from '../../../components'
import { PAGE_SIZE } from '../../../constants'

export const InvestmentsActivity: FC = () => {
  const { t } = useTranslation('features-transactions')
  const navigate = useNavigate()

  const { alias = '' } = useParams<{ alias: string }>()

  const { data: overviewData, isLoading: balanceDataLoading, isError: balanceDataError } = useWealthOverview()

  const investmentAccountDetail = overviewData?.personalPortfolio?.accounts.find(
    (account) => account.alias === parseInt(alias),
  )

  const {
    data: investmentAccountActivity,
    isLoading,
    isError,
    dataUpdatedAt,
  } = useInvestmentAccountActivity({
    alias: parseInt(alias),
  })

  const lastRefreshed = dataUpdatedAt ? getLastRefreshedText({ t, timestamp: dataUpdatedAt }) : undefined

  const accountValue = investmentAccountDetail?.balance.accountValue
  const investedBalance = investmentAccountDetail?.balance.investedBalance
  const cashBalance = investmentAccountDetail?.balance.cashBalance
  const returnSinceInception = investmentAccountDetail?.balance.amountSinceInception

  const [page, setPage] = useState<number>(1)

  const [loadingMoreTransactions, setLoadingMoreTransactions] = useState<boolean>(false)

  // Get total pages based on the number of transactions and the page size
  const getTotalPages = useMemo((): number => {
    if (!investmentAccountActivity?.activity) return 0
    return Math.ceil(investmentAccountActivity.activity.length / PAGE_SIZE)
  }, [investmentAccountActivity])

  // Load more transactions
  const onLoadMore = useCallback(() => {
    setLoadingMoreTransactions(true)

    setTimeout(() => {
      setLoadingMoreTransactions(false)
      setPage((currPage) => currPage + 1)
    }, 500)
  }, [])

  // Paginate transactions
  const paginatedTransactions = useMemo((): InvestmentActivityDetailV2[] => {
    if (!investmentAccountActivity?.activity) return []
    const endIndex = page * PAGE_SIZE
    return investmentAccountActivity.activity.slice(0, endIndex)
  }, [investmentAccountActivity, page])

  // Check if there are more transactions to load
  const hasMoreTransactions = useMemo((): boolean => {
    return page < getTotalPages
  }, [page, getTotalPages])

  return (
    <>
      {isLoading || balanceDataLoading ? (
        <div className="egw-container">
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-xl" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        </div>
      ) : null}

      {investmentAccountActivity && investmentAccountDetail ? (
        <InfiniteScrollWrapper
          onLoadMore={onLoadMore}
          hasMore={hasMoreTransactions}
          isLoading={loadingMoreTransactions}
        >
          <PageHeader
            title={t('account_activity.title')}
            subtitle={investmentAccountDetail.accountName}
            backLinkUrl={WEALTH_ROUTES.INVESTMENTS_ACCOUNT_ACTIVITY.backPath?.replace(':alias', alias.toString()) ?? ''}
            lastRefreshed={lastRefreshed}
          />
          <div className="egw-container py-0">
            <div className="flex flex-col gap-2xs mb-xl">
              <div className="flex justify-between">
                <h3 className="flex-3xs typo-primary-xl-semibold text-text-header">
                  {t('activity.investment.account_value')}
                </h3>
                <p>
                  {accountValue ? (
                    <span className="typo-primary-2xl-light text-text-meta">
                      {getCurrencySymbol(accountValue.currencyCode)}
                    </span>
                  ) : null}
                  <span className="typo-primary-2xl-semibold text-text-header">
                    {Currency.integerCents.onlyMinus(accountValue?.amount ?? 0)}
                  </span>
                </p>
              </div>

              <div className="flex justify-between">
                <p className="flex-3xs typo-primary-base-light text-text-meta">
                  {t('activity.investment.invested_balance')}
                </p>
                <div>
                  {investedBalance ? (
                    <span className="typo-primary-base-light text-text-meta">
                      {getCurrencySymbol(investedBalance.currencyCode)}
                    </span>
                  ) : null}
                  <span className="typo-primary-base-light text-text-meta">
                    {Currency.integerCents.onlyMinus(investedBalance?.amount ?? 0)}
                  </span>
                </div>
              </div>

              <div className="flex justify-between">
                <p className="flex-3xs typo-primary-base-light text-text-meta">
                  {t('activity.investment.cash_balance')}
                </p>
                <div>
                  {cashBalance ? (
                    <span className="typo-primary-base-light text-text-meta">
                      {getCurrencySymbol(cashBalance.currencyCode)}
                    </span>
                  ) : null}
                  <span className="typo-primary-base-light text-text-meta">
                    {Currency.integerCents.onlyMinus(cashBalance?.amount ?? 0)}
                  </span>
                </div>
              </div>

              <div className="flex justify-between">
                <p className="flex-3xs typo-primary-base-light text-text-meta">
                  {t('activity.investment.return_since_inception')}
                </p>
                <div>
                  {returnSinceInception ? (
                    <span className="typo-primary-base-light text-text-meta">
                      {getCurrencySymbol(returnSinceInception.currencyCode)}
                    </span>
                  ) : null}
                  <span className="typo-primary-base-light text-text-meta">
                    {Currency.integerCents.onlyMinus(returnSinceInception?.amount ?? 0)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="w-full border-b border-light mb-sm" />

          <div className="egw-container pt-0">
            {/* Transactions list */}
            {investmentAccountActivity.activity.map((transaction) => (
              <TransactionInvestmentItem
                key={transaction.activityId}
                transaction={transaction}
                alias={parseInt(alias)}
              />
            ))}

            {/* No transactions */}
            {investmentAccountActivity.activity.length === 0 && (
              <div className="bg-bglight flex flex-col items-center mt-lg py-md px-lg gap-md">
                <p className="text-text-header text-center typo-primary-2xl-light">
                  {t('activity.no_transactions.title')}
                </p>
                <p className="text-text-body text-center">{t('activity.no_transactions.description')}</p>
                <Button
                  variant={ButtonVariant.Primary}
                  onClick={() => {
                    navigate(WEALTH_ROUTES.TRANSFER.path)
                  }}
                >
                  {t('activity.no_transactions.transfer')}
                </Button>
              </div>
            )}

            {loadingMoreTransactions ? (
              <div className="w-full">
                <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
                  <Skeleton count={5} height="50px" />
                </SkeletonTheme>
              </div>
            ) : null}

            {!hasMoreTransactions && paginatedTransactions.length > 0 && (
              <div className="flex justify-center my-4 typo-primary-xs-regular text-text-meta">
                {t('activity.end_of_transactions')}
              </div>
            )}
          </div>
        </InfiniteScrollWrapper>
      ) : null}

      {isError || balanceDataError ? (
        <div className="egw-container">
          <InformationCircleIcon className="w-2xl h-2xl fill-red-500 mb-sm" />
          <p className="text-text-error typo-primary-2xl-light mb-xl">{t('common.generic_error')}</p>
          <p className="text-text-body typo-primary-xl">{t('activity.error')}</p>
        </div>
      ) : null}
    </>
  )
}
